<template>
    <div class="main-container overflow-x-auto">
        <div class="overflow-auto pa-5" style="grid-area: search">
            <h4 class="mb-2">{{ $t('mag_evt_srch_title') }}</h4>
            <p style="min-height: 50px">{{ $t('mag_evt_srch_txt') }}</p>
            <!-- search list -->
            <BaseRemoteList
                dense
                color="var(--tgk-grey)"
                session-key="magazin-events-search-list"
                :collection-class="EventCollection"
                :extra-params="searchQueryParams"
                :headers="searchHeaders"
                :options="{ sortBy: ['title'], sortDesc: [false] }"
                hide-default-footer
                hide-top-toolbar
                :new-btn="false"
                :reload-btn="false"
                disable-pagination
                @loading="loadingSearch = $event"
                @edit-item="onEdit"
            >
                <template #toolbar-items>
                    <span></span>
                </template>
                <template #[`item.assignBtn`]="{ item }">
                    <icon-btn
                        icon-cls="arrow-bigright"
                        icon-color="action"
                        plain
                        fab
                        :tooltip="$t('assign')"
                        @click="assignEvent(item)"
                    />
                </template>

                <template #after-query-field>
                    <div
                        :class="{
                            'ml-1': true,
                            'erw-suche-indicator': Object.keys(extendedSearchParams || {}).length > 0,
                        }"
                    >
                        <IconBtn
                            color="grey"
                            :disabled="loadingSearch"
                            icon-cls="advancedsearch"
                            icon-color="white"
                            icon-disabled-color="grey"
                            :tooltip="$t('erw_suche')"
                            @click="showErwSuche = true"
                        />
                    </div>
                </template>

                <template #[`item.next_schedule_date`]="{ item }">
                    {{ item.next_schedule_date_display }}
                </template>
            </BaseRemoteList>
        </div>

        <div class="overflow-auto assigned-panel pt-5 pl-5 pr-5" style="grid-area: assigned">
            <h4 class="mb-2">{{ $t('mag_evt_assg_title') }}</h4>
            <p style="min-height: 50px">{{ $t('mag_evt_assg_txt') }}</p>
            <!-- assigned list -->
            <BaseList
                dense
                color="var(--tgk-grey)"
                session-key="magazin-events-assigned-list"
                :loading="loadingAssigned"
                :items="assignedEventCollection.models"
                :headers="assignedHeaders"
                hide-default-footer
                hide-top-toolbar
                :reload-btn="false"
                :new-btn="false"
                :query-field="false"
                disable-pagination
                @edit-item="onEdit"
                @delete-item="deleteAssigned"
                @reload="loadAssignedEvents()"
            >
                <template #toolbar-items>
                    <span></span>
                </template>
                <template #[`item.next_schedule_date`]="{ item }">
                    {{ item.next_schedule_date_display }}
                </template>
            </BaseList>
        </div>
        <!-- enhanced search btn -->
        <v-navigation-drawer v-model="showErwSuche" absolute bottom temporary width="530px" color="kp-light-panel-bg">
            <v-toolbar dense class="kp-sublime-toolbar kp-root-panel-bg" elevation="0">
                <v-toolbar-title>{{ $t('erw_suche') }}</v-toolbar-title>
                <v-spacer />
                <IconBtn icon-cls="x" icon-color="grey" @click="showErwSuche = false" />
            </v-toolbar>
            <EventEnhancedSearch
                ref="search"
                style="grid-area: search; overflow: auto"
                :initial-params="extendedSearchParams"
                @search="onEnhancedSearch"
                @reset="resetEnhancedSearch"
            />
        </v-navigation-drawer>
    </div>
</template>

<script>
import { instance as api } from '@/helpers/api';
import { mapActions } from 'vuex';
import { errToStr } from '@/helpers/tools';
import EventEnhancedSearch from '@/components/event/EnhancedSearch.vue';
import sessionStorageMixin from '@/mixins/session-storage';
import { EventCollection } from '@/models/Event';
import { Magazin } from '@/models/Magazin';
import { getRouteParams } from '@/helpers/eventsOpenMode';

export default {
    name: 'MagazinEditEvents',
    components: {
        EventEnhancedSearch,
    },
    mixins: [sessionStorageMixin],

    props: {
        record: {
            type: Magazin,
            required: true,
        },
    },

    data() {
        const assignedEventCollection = new EventCollection();
        assignedEventCollection.addExtraParam('includeOlder', true);

        return {
            EventCollection,
            loadingAssigned: false,
            loadingSearch: false,
            showErwSuche: false,
            extendedSearchParams: this.getSessionValue('extendedSearchParams') || {},
            assignedEventCollection,
            assignedItemToDelete: null,
            assignedHeaders: [
                { text: this.$t('titel'), sortable: true, value: '$.title' },
                { text: this.$t('event_organizer'), sortable: true, value: '$.event_organizer', width: 180 },
                { text: this.$t('next_date'), sortable: true, value: 'next_schedule_date', width: 180 },
                { sortable: false, value: 'actions' },
            ],
            searchHeaders: [
                { text: this.$t('titel'), sortable: true, value: '$.title' },
                { text: this.$t('event_organizer'), sortable: true, value: '$.event_organizer', width: 180 },
                { text: this.$t('next_date'), sortable: true, value: 'next_schedule_date', width: 180 },
                { text: this.$t('assign'), sortable: false, value: 'assignBtn' },
            ],
        };
    },

    computed: {
        searchQueryParams() {
            return {
                status: 'released',
                enhancedFilters: this.extendedSearchParams,
            };
        },
    },

    beforeCreate() {
        this.sessionKey = 'magazin-edit-events';
    },

    mounted() {
        this.loadAssignedEvents();
    },

    methods: {
        ...mapActions(['runAsync']),

        async loadAssignedEvents() {
            this.loadingAssigned = true;
            this.assignedEventCollection.addExtraParam('magazin_id', this.record ? this.record.id : -1);
            await this.assignedEventCollection.query();
            this.loadingAssigned = false;
        },

        onEnhancedSearch(params) {
            this.extendedSearchParams = { ...params };
            this.showErwSuche = false;
        },

        resetEnhancedSearch() {
            this.extendedSearchParams = {};
            this.showErwSuche = false;
        },

        onEdit(event) {
            const { name, id } = getRouteParams(event);

            this.$router.push({
                name,
                params: { id, backRoute: { route: this.$route, displayText: this.$t('magazin') } },
            });
        },

        async assignEvent(event) {
            if (event) {
                try {
                    this.loadingAssigned = true;
                    await this.runAsync(() =>
                        api().post('/MagazinEvent/createLink', {
                            magazin_id: this.record.id,
                            event_id: event.id,
                        }),
                    );
                    this.loadAssignedEvents();
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                } finally {
                    this.loadingAssigned = false;
                }
            }
        },

        async deleteAssigned(event) {
            if (event) {
                try {
                    this.loadingAssigned = true;
                    await this.runAsync(() =>
                        api().post('/MagazinEvent/removeLinksForMagazin', {
                            magazin_id: this.record.id,
                            event_ids: JSON.stringify([event.id]),
                        }),
                    );
                    this.loadAssignedEvents();
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                } finally {
                    this.loadingAssigned = false;
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.main-container {
    display: grid;
    gap: 20px;
    grid-template-columns: minmax(400px, 1fr) 400px;
    grid-template-rows: auto;
    grid-template-areas: 'search assigned';
}
.assigned-panel {
    background-color: var(--toolbar-bg-color);
}
</style>
